import type { CustomFieldType, SupportedLocale } from '@erp/shared';

interface CustomFieldUsabilityMessages {
  unavailableChoice: string;
  unavailableDefaultHint: string;
  clearDefault: string;
  all: string;
  retry: string;
  startHint: string;
  fieldHint: string;
  categoryHint: string;
  toolsTitle: string;
  toolsHint: string;
  translationTools: string;
  expertTitle: string;
  expertHint: string;
  keyHint: string;
  rulesTitle: string;
  rulesHint: string;
  lengthRangeInvalid: string;
  numberRangeInvalid: string;
  permissionsTitle: string;
  permissionsHint: string;
  categoryAppearance: string;
  selectionDefaultHint: string;
  noOptionsHint: string;
  validationTitle: string;
  validationHint: string;
  invalidSetting: string;
  typeExamples: Record<CustomFieldType, string>;
}

export const customFieldUsabilityTranslations: Record<
  SupportedLocale,
  CustomFieldUsabilityMessages
> = {
  de: {
    lengthRangeInvalid:
      'Die maximale Länge darf nicht kleiner als die Mindestlänge sein. Passen Sie eine der Grenzen an.',
    numberRangeInvalid:
      'Der Höchstwert darf nicht kleiner als der Mindestwert sein. Passen Sie eine der Grenzen an.',
    unavailableChoice: 'Nicht mehr verfügbar',
    unavailableDefaultHint:
      'Eine bisherige Vorauswahl ist nicht mehr verfügbar. Entfernen Sie sie oder wählen Sie neu.',
    clearDefault: 'Vorauswahl entfernen',
    retry: 'Erneut versuchen',
    all: 'Alle',
    startHint:
      'Wählen Sie den Bereich, legen Sie eine Kategorie an und ergänzen Sie die benötigten Felder.',
    fieldHint:
      'Beginnen Sie mit Bezeichnung, Feldtyp und Kategorie. Weitere Einstellungen sind optional. Ein Entwurf kann auch ohne vollständige Übersetzungen gespeichert werden.',
    categoryHint:
      'Eine Kategorie fasst zusammengehörige Angaben zusammen, zum Beispiel Lieferhinweise. Pflegen Sie den Namen in allen angezeigten Sprachen; die weiteren Einstellungen sind optional.',
    toolsTitle: 'Weitere Verwaltungswerkzeuge',
    toolsHint:
      'Qualität prüfen, Übersetzungen bearbeiten, Freigaben einstellen und Konfigurationen übertragen.',
    translationTools: 'Übersetzungshilfe öffnen',
    expertTitle: 'Technische Einstellungen',
    expertHint:
      'Diese Angaben werden automatisch vorbelegt. Ändern Sie sie nur, wenn Ihre Schnittstelle oder ein besonderer Ablauf dies benötigt.',
    keyHint:
      'Wird aus der deutschen Bezeichnung automatisch erzeugt. Für die normale Einrichtung müssen Sie hier nichts eingeben. Nach dem Anlegen bleibt der Schlüssel fest.',
    rulesTitle: 'Eingaben begrenzen (optional)',
    rulesHint: 'Leer lassen, wenn keine zusätzlichen Grenzen erforderlich sind.',
    permissionsTitle: 'Zugriff zusätzlich einschränken',
    permissionsHint:
      'Die bestehenden Benutzerrechte gelten weiterhin. Zusätzliche Rechte nur auswählen, wenn dieses Feld oder diese Kategorie stärker eingeschränkt werden soll.',
    categoryAppearance: 'Darstellung anpassen (optional)',
    selectionDefaultHint:
      'Wählen Sie anhand der Bezeichnung. Der zugehörige Wert wird automatisch gespeichert.',
    noOptionsHint: 'Legen Sie zuerst die Auswahlmöglichkeiten an.',
    validationTitle: 'Bitte prüfen Sie die markierten Angaben.',
    validationHint:
      'Ihre Eingaben bleiben erhalten. Wählen Sie einen Eintrag, um die zugehörige Einstellung zu öffnen.',
    invalidSetting: 'Angabe prüfen',
    typeExamples: {
      WEEKLY_SCHEDULE:
        'Für wiederkehrende Wochentage mit einer oder mehreren frei gewählten Uhrzeiten.',
      TEXT: 'Für kurze Angaben, zum Beispiel eine Kundennummer oder einen Lieferhinweis.',
      TEXTAREA: 'Für längere Hinweise mit mehreren Zeilen.',
      NUMBER: 'Für ganze Zahlen, zum Beispiel die Anzahl der Verpackungen.',
      DECIMAL: 'Für Zahlen mit Nachkommastellen, zum Beispiel Gewicht oder Länge.',
      DATE: 'Für ein Kalenderdatum, zum Beispiel den nächsten Prüftermin.',
      CHECKBOX: 'Für eine Ja- oder Nein-Angabe, zum Beispiel „Kühlung erforderlich“.',
      SELECT: 'Für genau eine Auswahl aus einer festen Liste, zum Beispiel die Lieferart.',
      MULTI_SELECT: 'Für mehrere passende Einträge aus einer festen Liste.',
      FORMULA:
        'Berechnet einen Wert aus vorhandenen Angaben. Für die Einrichtung sind Kenntnisse der verwendeten Formel nötig.',
    },
  },
  en: {
    lengthRangeInvalid: 'Maximum length must be at least the minimum length. Adjust either limit.',
    numberRangeInvalid:
      'The maximum value must be at least the minimum value. Adjust either limit.',
    unavailableChoice: 'No longer available',
    unavailableDefaultHint:
      'A previous default choice is no longer available. Remove it or choose another option.',
    clearDefault: 'Clear default choices',
    retry: 'Try again',
    all: 'All',
    startHint: 'Choose an area, create a category and add the fields you need.',
    fieldHint:
      'Start with the label, field type and category. Other settings are optional. You can save a draft before all translations are complete.',
    categoryHint:
      'A category groups related information, such as delivery instructions. Enter its name in every language shown; other settings are optional.',
    toolsTitle: 'More administration tools',
    toolsHint:
      'Check quality, manage translations, configure approval and transfer configurations.',
    translationTools: 'Open translation assistance',
    expertTitle: 'Technical settings',
    expertHint:
      'These values are filled automatically. Change them only when an integration or a specific workflow requires it.',
    keyHint:
      'Generated automatically from the German label. Leave this blank for normal setup. The key cannot change after creation.',
    rulesTitle: 'Limit accepted values (optional)',
    rulesHint: 'Leave blank if no additional limits are needed.',
    permissionsTitle: 'Add access restrictions',
    permissionsHint:
      'Existing user permissions still apply. Select additional permissions only when this field or category needs stricter access.',
    categoryAppearance: 'Adjust appearance (optional)',
    selectionDefaultHint: 'Choose by label. The corresponding value is saved automatically.',
    noOptionsHint: 'Add the available choices first.',
    validationTitle: 'Please check the highlighted information.',
    validationHint: 'Your entries are kept. Select an item to open the corresponding setting.',
    invalidSetting: 'Check entry',
    typeExamples: {
      WEEKLY_SCHEDULE: 'For recurring weekdays with one or more freely chosen time windows.',
      TEXT: 'For short entries, such as a customer reference or delivery instruction.',
      TEXTAREA: 'For longer notes with several lines.',
      NUMBER: 'For whole numbers, such as the number of packages.',
      DECIMAL: 'For numbers with decimal places, such as weight or length.',
      DATE: 'For a calendar date, such as the next inspection date.',
      CHECKBOX: 'For a yes or no answer, such as “Refrigeration required”.',
      SELECT: 'For one choice from a fixed list, such as the delivery method.',
      MULTI_SELECT: 'For several matching choices from a fixed list.',
      FORMULA:
        'Calculates a value from existing information. Setup requires knowledge of the formula used.',
    },
  },
  fr: {
    lengthRangeInvalid:
      'La longueur maximale doit être au moins égale à la longueur minimale. Modifiez une des limites.',
    numberRangeInvalid:
      'La valeur maximale doit être au moins égale à la valeur minimale. Modifiez une des limites.',
    unavailableChoice: 'Plus disponible',
    unavailableDefaultHint:
      'Un choix par défaut précédent n’est plus disponible. Supprimez-le ou choisissez une autre option.',
    clearDefault: 'Effacer les choix par défaut',
    retry: 'Réessayer',
    all: 'Tous',
    startHint: 'Choisissez un domaine, créez une catégorie et ajoutez les champs nécessaires.',
    fieldHint:
      'Commencez par le libellé, le type de champ et la catégorie. Les autres réglages sont facultatifs. Vous pouvez enregistrer un brouillon avant de terminer les traductions.',
    categoryHint:
      'Une catégorie regroupe des informations liées, comme les consignes de livraison. Saisissez son nom dans chaque langue affichée ; les autres réglages sont facultatifs.',
    toolsTitle: 'Autres outils d’administration',
    toolsHint:
      'Vérifier la qualité, gérer les traductions, régler les validations et transférer les configurations.',
    translationTools: 'Ouvrir l’aide à la traduction',
    expertTitle: 'Réglages techniques',
    expertHint:
      'Ces valeurs sont renseignées automatiquement. Modifiez-les uniquement si une interface ou un processus particulier l’exige.',
    keyHint:
      'Générée automatiquement à partir du libellé allemand. Laissez ce champ vide pour une configuration courante. La clé est fixe après la création.',
    rulesTitle: 'Limiter les valeurs admises (facultatif)',
    rulesHint: 'Laissez vide si aucune limite supplémentaire n’est nécessaire.',
    permissionsTitle: 'Ajouter des restrictions d’accès',
    permissionsHint:
      'Les droits existants restent applicables. Sélectionnez des droits supplémentaires uniquement si ce champ ou cette catégorie nécessite un accès plus restreint.',
    categoryAppearance: 'Adapter la présentation (facultatif)',
    selectionDefaultHint:
      'Choisissez le libellé. La valeur correspondante est enregistrée automatiquement.',
    noOptionsHint: 'Ajoutez d’abord les choix disponibles.',
    validationTitle: 'Veuillez vérifier les informations signalées.',
    validationHint:
      'Vos saisies sont conservées. Sélectionnez une entrée pour ouvrir le réglage concerné.',
    invalidSetting: 'Vérifier la saisie',
    typeExamples: {
      WEEKLY_SCHEDULE: 'Pour des jours récurrents avec un ou plusieurs créneaux horaires au choix.',
      TEXT: 'Pour une information courte, comme une référence client ou une consigne de livraison.',
      TEXTAREA: 'Pour des remarques longues sur plusieurs lignes.',
      NUMBER: 'Pour des nombres entiers, comme le nombre de colis.',
      DECIMAL: 'Pour des nombres décimaux, comme un poids ou une longueur.',
      DATE: 'Pour une date, comme le prochain contrôle.',
      CHECKBOX: 'Pour une réponse oui ou non, comme « Réfrigération nécessaire ».',
      SELECT: 'Pour un choix dans une liste fixe, comme le mode de livraison.',
      MULTI_SELECT: 'Pour plusieurs choix dans une liste fixe.',
      FORMULA:
        'Calcule une valeur à partir des informations existantes. La configuration nécessite de connaître la formule utilisée.',
    },
  },
  es: {
    lengthRangeInvalid:
      'La longitud máxima debe ser igual o mayor que la mínima. Ajuste uno de los límites.',
    numberRangeInvalid:
      'El valor máximo debe ser igual o mayor que el mínimo. Ajuste uno de los límites.',
    unavailableChoice: 'Ya no disponible',
    unavailableDefaultHint:
      'Una opción predeterminada anterior ya no está disponible. Elimínela o elija otra.',
    clearDefault: 'Borrar selección predeterminada',
    retry: 'Volver a intentar',
    all: 'Todos',
    startHint: 'Elija un área, cree una categoría y añada los campos necesarios.',
    fieldHint:
      'Empiece por la etiqueta, el tipo de campo y la categoría. Los demás ajustes son opcionales. Puede guardar un borrador antes de completar las traducciones.',
    categoryHint:
      'Una categoría agrupa información relacionada, como las instrucciones de entrega. Introduzca su nombre en cada idioma mostrado; los demás ajustes son opcionales.',
    toolsTitle: 'Más herramientas de administración',
    toolsHint:
      'Comprobar la calidad, gestionar traducciones, configurar aprobaciones y transferir configuraciones.',
    translationTools: 'Abrir ayuda de traducción',
    expertTitle: 'Ajustes técnicos',
    expertHint:
      'Estos valores se rellenan automáticamente. Cámbielos solo si una integración o un proceso específico lo requiere.',
    keyHint:
      'Se genera automáticamente a partir de la etiqueta alemana. Déjelo vacío para la configuración habitual. La clave queda fija tras la creación.',
    rulesTitle: 'Limitar valores admitidos (opcional)',
    rulesHint: 'Déjelo vacío si no necesita límites adicionales.',
    permissionsTitle: 'Añadir restricciones de acceso',
    permissionsHint:
      'Los permisos existentes siguen vigentes. Seleccione permisos adicionales solo si este campo o categoría necesita un acceso más restringido.',
    categoryAppearance: 'Ajustar presentación (opcional)',
    selectionDefaultHint: 'Elija por etiqueta. El valor correspondiente se guarda automáticamente.',
    noOptionsHint: 'Añada primero las opciones disponibles.',
    validationTitle: 'Revise los datos señalados.',
    validationHint:
      'Sus entradas se conservan. Seleccione un elemento para abrir el ajuste correspondiente.',
    invalidSetting: 'Revisar dato',
    typeExamples: {
      WEEKLY_SCHEDULE: 'Para días recurrentes con una o varias franjas horarias de libre elección.',
      TEXT: 'Para datos breves, como una referencia de cliente o una instrucción de entrega.',
      TEXTAREA: 'Para notas extensas de varias líneas.',
      NUMBER: 'Para números enteros, como la cantidad de paquetes.',
      DECIMAL: 'Para números decimales, como el peso o la longitud.',
      DATE: 'Para una fecha, como la próxima inspección.',
      CHECKBOX: 'Para una respuesta sí o no, como «Requiere refrigeración».',
      SELECT: 'Para una opción de una lista fija, como el método de entrega.',
      MULTI_SELECT: 'Para varias opciones de una lista fija.',
      FORMULA:
        'Calcula un valor a partir de datos existentes. La configuración requiere conocer la fórmula utilizada.',
    },
  },
  tr: {
    lengthRangeInvalid:
      'Azami uzunluk, asgari uzunluktan küçük olamaz. Sınırlardan birini değiştirin.',
    numberRangeInvalid: 'Azami değer, asgari değerden küçük olamaz. Sınırlardan birini değiştirin.',
    unavailableChoice: 'Artık kullanılamıyor',
    unavailableDefaultHint:
      'Önceki bir varsayılan seçim artık kullanılamıyor. Kaldırın veya başka bir seçenek belirleyin.',
    clearDefault: 'Varsayılan seçimleri kaldır',
    retry: 'Yeniden dene',
    all: 'Tümü',
    startHint: 'Bir alan seçin, kategori oluşturun ve gereken alanları ekleyin.',
    fieldHint:
      'Etiket, alan türü ve kategori ile başlayın. Diğer ayarlar isteğe bağlıdır. Tüm çeviriler tamamlanmadan taslak kaydedebilirsiniz.',
    categoryHint:
      'Kategori, teslimat talimatları gibi ilgili bilgileri bir araya getirir. Adını gösterilen her dilde girin; diğer ayarlar isteğe bağlıdır.',
    toolsTitle: 'Diğer yönetim araçları',
    toolsHint:
      'Kaliteyi kontrol edin, çevirileri yönetin, onayları ayarlayın ve yapılandırmaları aktarın.',
    translationTools: 'Çeviri yardımını aç',
    expertTitle: 'Teknik ayarlar',
    expertHint:
      'Bu değerler otomatik doldurulur. Yalnızca bir entegrasyon veya özel iş akışı gerektiriyorsa değiştirin.',
    keyHint:
      'Almanca etiketten otomatik oluşturulur. Normal kurulumda boş bırakın. Anahtar oluşturulduktan sonra değiştirilemez.',
    rulesTitle: 'Kabul edilen değerleri sınırla (isteğe bağlı)',
    rulesHint: 'Ek sınır gerekmiyorsa boş bırakın.',
    permissionsTitle: 'Ek erişim kısıtlamaları',
    permissionsHint:
      'Mevcut kullanıcı izinleri geçerliliğini korur. Ek izinleri yalnızca bu alan veya kategori daha sıkı erişim gerektiriyorsa seçin.',
    categoryAppearance: 'Görünümü ayarla (isteğe bağlı)',
    selectionDefaultHint: 'Etikete göre seçin. İlgili değer otomatik kaydedilir.',
    noOptionsHint: 'Önce kullanılabilir seçenekleri ekleyin.',
    validationTitle: 'İşaretlenen bilgileri kontrol edin.',
    validationHint: 'Girdileriniz korunur. İlgili ayarı açmak için bir öğe seçin.',
    invalidSetting: 'Girdiyi kontrol et',
    typeExamples: {
      WEEKLY_SCHEDULE:
        'Tekrarlanan günler için serbestçe seçilen bir veya daha fazla zaman aralığı.',
      TEXT: 'Müşteri referansı veya teslimat talimatı gibi kısa bilgiler için.',
      TEXTAREA: 'Birden çok satırdan oluşan uzun notlar için.',
      NUMBER: 'Paket sayısı gibi tam sayılar için.',
      DECIMAL: 'Ağırlık veya uzunluk gibi ondalıklı sayılar için.',
      DATE: 'Bir sonraki kontrol tarihi gibi takvim tarihleri için.',
      CHECKBOX: '“Soğutma gerekli” gibi evet veya hayır yanıtları için.',
      SELECT: 'Teslimat yöntemi gibi sabit bir listeden tek seçim için.',
      MULTI_SELECT: 'Sabit bir listeden birden fazla uygun seçim için.',
      FORMULA:
        'Mevcut bilgilerden bir değer hesaplar. Kurulum için kullanılan formülü bilmek gerekir.',
    },
  },
};
